import React, { useState, useEffect, useRef } from 'react';
import { X, Save, Upload, StickyNote, Users, FolderOpen, Loader2 } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

function authHeaders(json = true) {
  const token = localStorage.getItem('token') || localStorage.getItem('access_token') || '';
  const h = token ? { Authorization: `Bearer ${token}` } : {};
  if (json) h['Content-Type'] = 'application/json';
  return h;
}

const TITLES = {
  stewardnotes: { title: 'Add Steward Note',       Icon: StickyNote, color: '#a78bfa' },
  witnesses:    { title: 'Add Witness Statement',  Icon: Users,      color: '#c084fc' },
  evidence:     { title: 'Upload Evidence',        Icon: FolderOpen, color: '#facc15' },
};

/**
 * ActionSlideOver — right-side slide-over that hosts one of three inline
 * forms so stewards can add a note / witness / evidence file without
 * leaving the Ask Stewie answer page.
 *
 * Props:
 *   type       'stewardnotes' | 'witnesses' | 'evidence'
 *   caseId     the Case Locker case id (parent's job to ensureCase first)
 *   onClose    close handler
 *   onResult   ({ok, text}) => void — invoked with a toast payload
 */
export default function ActionSlideOver({ type, caseId, onClose, onResult }) {
  const meta = TITLES[type];
  const closeBtnRef = useRef(null);

  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape') onClose(); }
    document.addEventListener('keydown', onKey);
    // focus close for keyboard users
    setTimeout(() => closeBtnRef.current?.focus(), 60);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (!meta) return null;

  return (
    <div
      className="aso-overlay"
      onClick={onClose}
      data-testid={`slideover-${type}-overlay`}
    >
      <style>{sheetStyles}</style>
      <aside
        className="aso-panel"
        role="dialog"
        aria-modal="true"
        aria-label={meta.title}
        onClick={(e) => e.stopPropagation()}
        data-testid={`slideover-${type}`}
      >
        <header className="aso-head" style={{ borderColor: meta.color + '55' }}>
          <span className="aso-head-icon" style={{ background: meta.color + '22', color: meta.color }}>
            <meta.Icon size={16} strokeWidth={2.2} />
          </span>
          <h3 className="aso-title">{meta.title}</h3>
          <button
            ref={closeBtnRef}
            type="button"
            className="aso-close"
            onClick={onClose}
            aria-label="Close"
            data-testid={`slideover-${type}-close`}
          >
            <X size={16} />
          </button>
        </header>

        <div className="aso-body">
          {type === 'stewardnotes' && (
            <NoteForm caseId={caseId} color={meta.color} onResult={onResult} onClose={onClose} />
          )}
          {type === 'witnesses' && (
            <WitnessForm caseId={caseId} color={meta.color} onResult={onResult} onClose={onClose} />
          )}
          {type === 'evidence' && (
            <EvidenceForm caseId={caseId} color={meta.color} onResult={onResult} onClose={onClose} />
          )}
        </div>
      </aside>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────── */

function NoteForm({ caseId, color, onResult, onClose }) {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);

  async function save() {
    if (!text.trim()) return;
    setBusy(true);
    try {
      const res = await axios.post(
        `${API}/api/case-locker/cases/${caseId}/timeline`,
        { action_type: 'note_added', description: text.trim() },
        { headers: authHeaders() },
      );
      if (res?.data?.ok !== false) {
        onResult?.({ ok: true, text: 'Note added to the case timeline.' });
        onClose();
      } else {
        onResult?.({ ok: false, text: res?.data?.detail || 'Could not save note.' });
      }
    } catch (e) {
      onResult?.({ ok: false, text: 'Network error saving note.' });
    } finally { setBusy(false); }
  }

  return (
    <div className="aso-form">
      <label className="aso-label">
        <span>Note</span>
        <textarea
          className="aso-textarea"
          rows={6}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Anything you want future-you to remember about this issue…"
          data-testid="slideover-note-input"
          autoFocus
        />
      </label>
      <p className="aso-hint">Notes appear in the case timeline and are visible to any steward you invite.</p>
      <SaveBar
        color={color}
        busy={busy}
        disabled={!text.trim()}
        onSave={save}
        onClose={onClose}
        saveTestId="slideover-note-save"
        cancelTestId="slideover-note-cancel"
      />
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────── */

function WitnessForm({ caseId, color, onResult, onClose }) {
  const [name, setName] = useState('');
  const [statement, setStatement] = useState('');
  const [busy, setBusy] = useState(false);

  async function save() {
    if (!name.trim()) return;
    setBusy(true);
    try {
      // 1) Fetch current witnesses (PUT is a replace on the field).
      const current = await axios.get(
        `${API}/api/case-locker/cases/${caseId}`,
        { headers: authHeaders(false) },
      );
      const existing = current?.data?.case?.witnesses || [];
      const entry = statement.trim()
        ? `${name.trim()} — ${statement.trim()}`
        : name.trim();
      // 2) Append and PUT.
      await axios.put(
        `${API}/api/case-locker/cases/${caseId}`,
        { witnesses: [...existing, entry] },
        { headers: authHeaders() },
      );
      onResult?.({ ok: true, text: `Added ${name.trim()} to witnesses.` });
      onClose();
    } catch (e) {
      onResult?.({ ok: false, text: 'Could not save witness. Try again.' });
    } finally { setBusy(false); }
  }

  return (
    <div className="aso-form">
      <label className="aso-label">
        <span>Witness name / role</span>
        <input
          type="text"
          className="aso-input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. John Smith, foreman"
          data-testid="slideover-witness-name"
          autoFocus
        />
      </label>
      <label className="aso-label">
        <span>What they saw / heard (optional)</span>
        <textarea
          className="aso-textarea"
          rows={5}
          value={statement}
          onChange={(e) => setStatement(e.target.value)}
          placeholder="Brief description — full statements can be recorded later in Fact Finder."
          data-testid="slideover-witness-statement"
        />
      </label>
      <SaveBar
        color={color}
        busy={busy}
        disabled={!name.trim()}
        onSave={save}
        onClose={onClose}
        saveTestId="slideover-witness-save"
        cancelTestId="slideover-witness-cancel"
      />
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────── */

const MAX_UPLOAD_BYTES = 20 * 1024 * 1024;
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'application/pdf'];

function EvidenceForm({ caseId, color, onResult, onClose }) {
  const [file, setFile] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  function onPick(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!ALLOWED_TYPES.includes(f.type)) { setErr('Only JPG, PNG, or PDF files.'); return; }
    if (f.size > MAX_UPLOAD_BYTES) { setErr(`File too large: ${(f.size / (1024*1024)).toFixed(1)}MB (max 20MB).`); return; }
    setErr('');
    setFile(f);
  }

  async function save() {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(
        `${API}/api/case-locker/cases/${caseId}/attachments`,
        fd,
        { headers: authHeaders(false) },
      );
      if (res?.data?.ok !== false) {
        onResult?.({ ok: true, text: `Uploaded ${file.name}.` });
        onClose();
      } else {
        onResult?.({ ok: false, text: res?.data?.detail || 'Upload failed.' });
      }
    } catch (e) {
      onResult?.({ ok: false, text: 'Upload failed. Try again.' });
    } finally { setBusy(false); }
  }

  return (
    <div className="aso-form">
      <div
        className="aso-dropzone"
        onClick={() => inputRef.current?.click()}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') inputRef.current?.click(); }}
        role="button"
        tabIndex={0}
        data-testid="slideover-evidence-dropzone"
      >
        <Upload size={22} />
        <div className="aso-dropzone-title">
          {file ? file.name : 'Click to choose a file'}
        </div>
        <div className="aso-dropzone-sub">
          {file
            ? `${(file.size / (1024*1024)).toFixed(2)}MB · ${file.type}`
            : 'JPG · PNG · PDF · up to 20MB'}
        </div>
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,application/pdf"
          onChange={onPick}
          style={{ display: 'none' }}
          data-testid="slideover-evidence-file"
        />
      </div>
      {err && <div className="aso-error" data-testid="slideover-evidence-error">{err}</div>}
      <p className="aso-hint">Uploaded files are attached to the case and appear in the Case Locker.</p>
      <SaveBar
        color={color}
        busy={busy}
        disabled={!file}
        onSave={save}
        onClose={onClose}
        saveLabel="Upload"
        saveTestId="slideover-evidence-save"
        cancelTestId="slideover-evidence-cancel"
      />
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────── */

function SaveBar({ color, busy, disabled, onSave, onClose, saveLabel = 'Save', saveTestId, cancelTestId }) {
  return (
    <div className="aso-actions">
      <button
        type="button"
        className="aso-btn aso-btn-secondary"
        onClick={onClose}
        disabled={busy}
        data-testid={cancelTestId}
      >
        Cancel
      </button>
      <button
        type="button"
        className="aso-btn aso-btn-primary"
        onClick={onSave}
        disabled={disabled || busy}
        style={{ '--aso-c': color }}
        data-testid={saveTestId}
      >
        {busy ? <Loader2 size={14} className="aso-spin" /> : <Save size={14} />}
        {busy ? 'Saving…' : saveLabel}
      </button>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────── */

const sheetStyles = `
.aso-overlay {
  position: fixed; inset: 0; z-index: 110;
  background: rgba(6, 17, 31, 0.62);
  backdrop-filter: blur(6px);
  display: flex; justify-content: flex-end;
  animation: aso-fade 180ms ease-out;
}
@keyframes aso-fade { from { opacity: 0; } to { opacity: 1; } }

.aso-panel {
  width: min(480px, 100vw);
  height: 100vh;
  background: #0d1b2e;
  border-left: 1px solid rgba(148,163,184,0.18);
  box-shadow: -20px 0 60px rgba(0,0,0,0.55);
  display: flex; flex-direction: column;
  animation: aso-slide 220ms cubic-bezier(0.16, 1, 0.3, 1);
  font-family: -apple-system, BlinkMacSystemFont, "Inter", "Segoe UI", system-ui, sans-serif;
}
@keyframes aso-slide {
  from { transform: translateX(100%); }
  to   { transform: translateX(0); }
}

.aso-head {
  display: flex; align-items: center; gap: 10px;
  padding: 14px 16px;
  border-bottom: 1px solid;
}
.aso-head-icon {
  width: 30px; height: 30px;
  border-radius: 8px;
  display: grid; place-items: center;
  border: 1px solid currentColor;
  flex-shrink: 0;
}
.aso-title {
  flex: 1;
  color: #f1f5f9;
  font-size: 15px; font-weight: 800; letter-spacing: -0.01em;
  margin: 0;
}
.aso-close {
  background: rgba(255,255,255,0.05);
  border: 1px solid rgba(255,255,255,0.10);
  color: #94a3b8;
  border-radius: 8px;
  width: 30px; height: 30px;
  display: grid; place-items: center;
  cursor: pointer;
}
.aso-close:hover { background: rgba(255,255,255,0.10); color: #f1f5f9; }

.aso-body { flex: 1; overflow-y: auto; padding: 16px; }

.aso-form { display: flex; flex-direction: column; gap: 14px; }
.aso-label {
  display: flex; flex-direction: column; gap: 6px;
}
.aso-label > span {
  font-size: 11px; font-weight: 800; letter-spacing: 0.1em;
  color: #94a3b8; text-transform: uppercase;
}
.aso-input, .aso-textarea {
  width: 100%;
  background: rgba(15,27,70,0.6);
  border: 1px solid rgba(148,163,184,0.22);
  border-radius: 10px;
  padding: 10px 12px;
  color: #f1f5f9;
  font-size: 13.5px;
  font-family: inherit;
  transition: border-color 150ms;
}
.aso-input:focus, .aso-textarea:focus {
  outline: none;
  border-color: rgba(103,232,249,0.55);
  background: rgba(15,27,70,0.85);
}
.aso-textarea { resize: vertical; line-height: 1.5; }

.aso-hint {
  color: #94a3b8;
  font-size: 12px; line-height: 1.5;
  margin: 0;
}

.aso-dropzone {
  display: flex; flex-direction: column; align-items: center; gap: 6px;
  padding: 22px 16px;
  border: 2px dashed rgba(148,163,184,0.28);
  border-radius: 12px;
  background: rgba(15,27,70,0.5);
  color: #cbd5e1;
  cursor: pointer;
  transition: border-color 160ms, background 160ms;
}
.aso-dropzone:hover, .aso-dropzone:focus {
  outline: none;
  border-color: rgba(103,232,249,0.55);
  background: rgba(15,27,70,0.7);
}
.aso-dropzone-title { font-size: 13px; font-weight: 700; color: #f1f5f9; }
.aso-dropzone-sub   { font-size: 11px; color: #94a3b8; }

.aso-error {
  background: rgba(239,68,68,0.10);
  border: 1px solid rgba(239,68,68,0.45);
  color: #fecaca;
  font-size: 12px; font-weight: 600;
  padding: 8px 12px;
  border-radius: 10px;
}

.aso-actions {
  display: flex; justify-content: flex-end; gap: 8px;
  margin-top: 4px;
  padding-top: 12px;
  border-top: 1px solid rgba(148,163,184,0.14);
}
.aso-btn {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 8px 14px;
  border-radius: 10px;
  font-size: 13px; font-weight: 700;
  cursor: pointer;
  font-family: inherit;
  transition: background 160ms, border-color 160ms, transform 160ms;
}
.aso-btn:disabled { opacity: 0.55; cursor: not-allowed; }
.aso-btn-secondary {
  background: transparent;
  border: 1px solid rgba(148,163,184,0.30);
  color: #cbd5e1;
}
.aso-btn-secondary:hover:not(:disabled) {
  background: rgba(255,255,255,0.04); color: #f1f5f9;
}
.aso-btn-primary {
  background: var(--aso-c, #67e8f9);
  border: 1px solid var(--aso-c, #67e8f9);
  color: #06111F;
}
.aso-btn-primary:hover:not(:disabled) {
  transform: translateY(-1px);
  filter: brightness(1.1);
}

.aso-spin { animation: aso-spin-kf 1.2s linear infinite; }
@keyframes aso-spin-kf { to { transform: rotate(360deg); } }
`;
